/**
 * "Oldest Xd" inline KPI text rendered in the Results toolbar's right
 * zone, before the "Showing X-Y of N" count. The two pieces sit as a
 * clearer metadata group separated by a vertical divider:
 *
 *     Oldest 10d | Showing 1-50 of 290
 *
 * Sources ``oldest_open_detected_at`` from the response envelope (see
 * ``lifecycle_envelope_totals`` for the backend contract).
 *
 * Renders nothing when:
 *   - response has no ``oldest_open_detected_at``
 *   - current view is the Closed slice (no open items to age)
 *
 * Styling: "Oldest" reads as muted grey label. The numeric value sits
 * in medium-weight darker text by default, shifting to amber at 14
 * days and red at 30 - so the colour cue stays on the value, not the
 * surrounding label.
 */
import React from 'react'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { readExceptionStatus } from './exceptionStatus'

const AMBER_DAYS = 14
const RED_DAYS = 30

const valueColorForDays = (days: number): string => {
  if (days >= RED_DAYS) return '#C62828'
  if (days >= AMBER_DAYS) return '#EF6C00'
  return 'text.primary'
}

interface OldestOpenChipProps {
  response: Record<string, any> | null | undefined
  searchParams: URLSearchParams
}

const OldestOpenChip: React.FC<OldestOpenChipProps> = ({
  response,
  searchParams,
}) => {
  if (!response) return null
  if (readExceptionStatus(searchParams) === 'closed') return null

  const detectedAt =
    typeof response.oldest_open_detected_at === 'string'
      ? response.oldest_open_detected_at
      : null
  if (!detectedAt) return null

  const parsed = Date.parse(detectedAt)
  if (Number.isNaN(parsed)) return null
  const days = Math.max(0, Math.floor((Date.now() - parsed) / 86_400_000))

  return (
    <Stack direction="row" alignItems="center" gap={1.25}>
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: '0.875rem',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.4,
        }}
      >
        Oldest{' '}
        <Box
          component="span"
          sx={{ color: valueColorForDays(days), fontWeight: 600 }}
        >
          {days}d
        </Box>
      </Typography>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          height: 16,
          alignSelf: 'center',
          borderColor: 'rgba(0, 0, 0, 0.16)',
        }}
      />
    </Stack>
  )
}

export default OldestOpenChip
